import { ArrowRight, CircleAlert, LockKeyhole, Mail, UserRound } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { INVESTOR_CERTIFICATION_TEXT } from "./AccreditedInvestorGate";
import { GateNudgeAnnouncement, GateNudgeRings } from "./GateNudge";
import GoogleSignInButton from "./GoogleSignInButton";
import type { ShareGateFlow } from "../hooks/useProductShareGateFlow";
import configService from "../services/configService";
import { isXInAppBrowser } from "../utils/inAppBrowser";
import { formatNumber } from "../utils/numberUtils";

export const GATE_EMAIL_INPUT_ID = "gate-email";

// React 19 knows `inert` as a boolean attribute; spread so the typings in use do not have to.
const INERT = { inert: true } as Record<string, boolean>;

export interface ProductShareGatePanelProps {
  flow: ShareGateFlow;
  total: number;
  /** The page panel only ever shows the email step; the modal shows both. */
  placement: "page" | "modal";
  onLoginClick: () => void;
}

// Panel copy uses a display heading, 15px body text, and 12px secondary text.
// The certification notice uses 6px text; the code digits are the one accent.
const inputClassName = "min-h-12 w-full min-w-0 flex-1 rounded-v7-control border border-v7-ink/25 bg-v7-white px-3.5 text-base sm:text-[15px] leading-[22px] text-v7-ink outline-none placeholder:text-v7-ink/40 transition-colors duration-200 hover:border-v7-ink/60 focus:border-v7-ink focus:ring-[3px] focus:ring-v7-ink/10 disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/40";
const buttonClassName = "inline-flex min-h-12 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-v7-control border border-v7-ink bg-v7-ink px-[18px] text-[15px] font-medium text-v7-white transition-colors duration-200 hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-45";
const linkClassName = "text-v7-ink underline underline-offset-2 transition-colors hover:text-v7-bronze-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark";

function ErrorNotice({ id, message }: { id: string; message: string }) {
  return (
    <div
      id={id}
      role="alert"
      className="flex items-start gap-2 rounded-v7-control border border-v7-rust/30 bg-v7-rust/5 p-3 text-left text-xs leading-[18px] text-v7-rust"
    >
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

/**
 * The sign-up panel from V5D: copy on the left, form on the right, as the
 * holdings card's beige footer or lifted into the modal. One component for
 * both so the typed values carry between them.
 */
const ProductShareGatePanel: React.FC<ProductShareGatePanelProps> = ({
  flow,
  total,
  placement,
  onLoginClick,
}) => {
  const location = useLocation();
  const codeInputRef = useRef<HTMLInputElement>(null);
  const isCodeStep = placement === "modal" && flow.step === "code";
  const idSuffix = placement === "modal" ? "m" : "p";
  const errorId = `gate-error-${idSuffix}`;
  const certificationNoticeId = `gate-certification-notice-${idSuffix}`;
  const certificationNotice = (
    <p id={certificationNoticeId} className="text-[6px] leading-[9px] text-v7-ink/60 dark:text-v7-ink/60">
      By signing in, you are certifying: {INVESTOR_CERTIFICATION_TEXT}
    </p>
  );

  useEffect(() => {
    if (isCodeStep) codeInputRef.current?.focus({ preventScroll: true });
  }, [isCodeStep]);

  // Google sign-in was most logins before the gate; back on the email step
  // (James, 2026-10-09). Not inside the X app's browser, where Google refuses
  // to complete, and not without a configured client.
  const googleAvailable = configService.isGoogleSignInEnabled()
    && Boolean(configService.getGoogleClientId())
    && !isXInAppBrowser();
  // Locked while a code request or a Google sign-in is in flight (isBusy). Google's button also locks itself, but its
  // lock is component-local: opening or closing the modal mounts a fresh button, which must not start a second sign-in.
  const googleLocked = flow.isBusy;
  // One Google button at a time: Google keeps only the last initialize() callback, so while the modal's button is
  // mounted the page panel keeps the space but not the button, and remounts it (re-initializing) when the modal closes.
  const showGoogleButton = placement === "modal" || !flow.isModalOpen;

  const left = isCodeStep ? (
    <>
      <h3 className="text-[26px] font-light leading-tight tracking-[-0.6px] text-v7-ink">Check your email.</h3>
      <p className="flex items-center gap-2 text-[15px] leading-[22px] text-v7-ink/60">
        <Mail className="h-3.5 w-3.5 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
        <span>We sent a 6-digit code to <b className="font-medium text-v7-ink">{flow.email}</b>.</span>
      </p>
      <p className="flex items-center gap-2 text-xs text-v7-ink/60">
        <UserRound className="h-3.5 w-3.5 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
        <span>
          Wrong address?{" "}
          <button type="button" onClick={flow.editEmail} className={linkClassName}>Edit it</button>
        </span>
      </p>
    </>
  ) : (
    <>
      <h3 className="text-[26px] font-light leading-tight tracking-[-0.6px] text-v7-ink">See the whole portfolio.</h3>
      <p className="flex items-center gap-2 text-[15px] leading-[22px] text-v7-ink/60">
        <LockKeyhole className="h-3.5 w-3.5 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
        <span>Free with an email.</span>
      </p>
      <p className="flex items-center gap-2 text-xs text-v7-ink/60">
        <UserRound className="h-3.5 w-3.5 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
        <span>
          Already on Plutus?{" "}
          <Link to="/login" state={{ from: flow.productPath ?? location, fromProductShare: flow.productPath !== null }} onClick={onLoginClick} className={linkClassName}>Log in</Link>
        </span>
      </p>
    </>
  );

  const right = isCodeStep ? (
    <form
      onSubmit={(event) => { event.preventDefault(); void flow.submitCode(); }}
      className="flex flex-col gap-4"
      aria-describedby={flow.error ? `${certificationNoticeId} ${errorId}` : certificationNoticeId}
    >
      <div className="flex flex-col gap-2 sm:flex-row">
        <label htmlFor={`gate-otp-${idSuffix}`} className="sr-only">One-time code</label>
        <input
          ref={codeInputRef}
          id={`gate-otp-${idSuffix}`}
          name="otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]{6}"
          required
          maxLength={6}
          value={flow.otp}
          onChange={(event) => flow.setOtp(event.target.value)}
          placeholder="······"
          disabled={flow.isBusy}
          aria-invalid={Boolean(flow.error)}
          className={`${inputClassName} text-center font-mono text-xl tracking-[10px]`}
        />
        <button type="submit" disabled={flow.isBusy || flow.otp.length !== 6} className={buttonClassName}>
          {flow.isSubmitting ? "Verifying..." : "Continue"}
          {!flow.isSubmitting && <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
        </button>
      </div>
      {flow.error && <ErrorNotice id={errorId} message={flow.error} />}
      {certificationNotice}
      <p className="text-xs text-v7-ink/60">
        Didn&apos;t get it?{" "}
        <button
          type="button"
          onClick={() => void flow.resendCode()}
          disabled={flow.isBusy || flow.resendCooldown > 0}
          className={`${linkClassName} disabled:cursor-not-allowed disabled:text-v7-ink/35 disabled:no-underline`}
        >
          {flow.resendCooldown > 0
            ? `Send it again in ${Math.floor(flow.resendCooldown / 60)}:${(flow.resendCooldown % 60).toString().padStart(2, "0")}`
            : "Send it again"}
        </button>
      </p>
    </form>
  ) : (
    <form
      onSubmit={(event) => { event.preventDefault(); void flow.submitEmail(); }}
      noValidate
      className="flex flex-col gap-3.5"
      aria-describedby={flow.error ? `${certificationNoticeId} ${errorId}` : certificationNoticeId}
    >
      {googleAvailable && (
        <>
          <div className="relative min-h-10 w-full max-w-[400px] self-center">
            {/* Google's iframe cannot be disabled directly. Keep its wrapper inert while sign-in is in flight. */}
            {showGoogleButton && (
              <div {...(googleLocked ? INERT : {})} aria-hidden={googleLocked || undefined}>
                <GoogleSignInButton text="continue_with" onSuccess={flow.completeGoogleSignIn} onBusyChange={flow.setGoogleBusy} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-v7-ink/15" />
            <span className="text-xs font-medium uppercase tracking-[0.14em] text-v7-ink/50">or with email</span>
            <span className="h-px flex-1 bg-v7-ink/15" />
          </div>
        </>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <div className="relative flex min-w-0 rounded-v7-control">
          <label htmlFor={`${GATE_EMAIL_INPUT_ID}-${idSuffix}`} className="sr-only">Email address</label>
          <input
            id={`${GATE_EMAIL_INPUT_ID}-${idSuffix}`}
            data-gate-email
            name="email"
            type="email"
            autoComplete="email"
            required
            value={flow.email}
            onChange={(event) => flow.setEmail(event.target.value)}
            placeholder="you@email.com"
            disabled={flow.isBusy}
            aria-invalid={flow.nudge.reason === "email" || Boolean(flow.error)}
            className={`${inputClassName} ${flow.nudge.reason === "email" ? "gate-nudged" : ""}`}
          />
          <GateNudgeRings active={flow.nudge.reason === "email"} sequence={flow.nudge.sequence} />
        </div>
        <button data-gate-unlock type="submit" disabled={flow.isBusy} className={`${buttonClassName} sm:col-start-2 sm:row-start-1`}>
          {flow.isSubmitting ? (
            "Sending code..."
          ) : (
            <span>Unlock All {formatNumber(total, 0)} <span className="text-v7-bronze">Free</span></span>
          )}
        </button>
      </div>
      {flow.error && <ErrorNotice id={errorId} message={flow.error} />}
      {certificationNotice}
      <GateNudgeAnnouncement reason={flow.nudge.reason} sequence={flow.nudge.sequence} />
    </form>
  );

  return (
    <div
      data-testid={`product-share-gate-${placement}`}
      data-gate-step={isCodeStep ? "code" : "email"}
      className="grid gap-[18px] border-t border-v7-ink/15 bg-v7-paper px-[18px] py-5 min-[820px]:grid-cols-[1fr_1.15fr] min-[820px]:gap-8 min-[820px]:px-7 min-[820px]:py-6"
    >
      <div className="flex flex-col gap-2.5 sm:gap-3 md:justify-center md:gap-4">{left}</div>
      <div className="min-w-0">{right}</div>
    </div>
  );
};

export default ProductShareGatePanel;
